import {
  CalendarDaysIcon,
  DocumentTextIcon,
  PencilIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import TextWinds from '../../../common/TextWinds';
import {
  AssignmentKindType,
  AssignmentStatusByClassItem,
  AssignmentStatusDetailByClassItem,
} from '../../../../constants/types/lms/assignment/type';
import {
  formatToKstDate,
  getErrorMessages,
} from '../../../../api/utils/util-func';
import { openAlert, openConfirm } from '../../../../store/features/modalSlice';
import {
  deleteAssignment,
  getAssignmentStatusDetailListByClass,
  updateAssignmentToDate,
} from '../../../../api/utils/lms/assignment/api';
import { ElementType, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../store/hook';
import { LoadingSpinner } from '../../../Loading/LoadingSpinner';
import AssignmentStatusDetailList from './AssignmentStatusDetailList';
import { AssignmentStatusType } from '../../../../constants/types/lms/type';
import {
  getProgressColorClassNames,
  getProgressRate,
} from '../../../../pages/lms/assignment-mgmt/AssignmentStatus';
import dayjs, { Dayjs } from 'dayjs';

interface AssignmentStatusByClassCardProps {
  assignmentStatus: AssignmentStatusByClassItem;
  fetchList: () => Promise<void>;
}

const ASSIGNMENT_KIND_META_MAP: Record<
  AssignmentKindType,
  {
    label: string;
    className: string;
    icon: ElementType;
  }
> = {
  [AssignmentKindType.TEST]: {
    label: '테스트',
    className: 'bg-blue-50 text-[#0B8ED0]',
    icon: DocumentTextIcon,
  },
  [AssignmentKindType.ASSIGNMENT]: {
    label: '과제',
    className: 'bg-green-50 text-green-600',
    icon: PencilSquareIcon,
  },
};

const getAssignmentCompletionSummary = (
  detailList: AssignmentStatusDetailByClassItem[] = [],
) => {
  const totalCount = detailList.length;

  const completedCount = detailList.filter(
    (item) => item.progress.progressState === 'COMPLETED',
  ).length;

  const progress =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const completedRate = getProgressRate(progress);

  const isAllCompleted = totalCount > 0 && completedCount === totalCount;

  return {
    totalCount,
    completedCount,
    completedRate,
    isAllCompleted,
  };
};

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 현황 페이지 > 반별 과제 현황 > 반별 과제 카드
 */
const AssignmentStatusByClassCard = ({
  assignmentStatus,
  fetchList,
}: AssignmentStatusByClassCardProps) => {
  const { txId, txInfo } = assignmentStatus;

  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [assignmentStatusDetailList, setAssignmentStatusDetailList] = useState<
    AssignmentStatusDetailByClassItem[]
  >([]);

  useEffect(() => {
    if (!classId) return;

    const fetchAssignmentStatusDetailByClass = async () => {
      try {
        setIsLoading(true);

        const response = await getAssignmentStatusDetailListByClass({
          classId,
          detailId: txId,
        });

        setAssignmentStatusDetailList(response ?? []);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssignmentStatusDetailByClass();
  }, [classId, dispatch]);

  const handleDelete = async (
    txId: string,
    toDoStatus: AssignmentStatusType,
  ) => {
    dispatch(
      openConfirm({
        message: (
          <div className="flex flex-col md:my-3">
            <div className="text-base">삭제하시면 되돌릴 수 없습니다.</div>

            <div className="text-base">정말 삭제하시겠습니까?</div>
          </div>
        ),
        confirmText: '삭제하기',
        cancelText: '닫기',
        onConfirm: async () => {
          try {
            await deleteAssignment({
              classId,
              txId,
              todoStatus: toDoStatus,
            });

            await fetchList();
          } catch (error) {
            dispatch(openAlert({ message: getErrorMessages(error) }));
          }
        },
      }),
    );
  };

  const assignmentKindMeta = ASSIGNMENT_KIND_META_MAP[txInfo.todoKind] ?? {
    label: '과제',
    className: 'bg-gray1 text-gray7',
    icon: DocumentTextIcon,
  };

  const AssignmentKindIcon = assignmentKindMeta.icon;

  const {
    totalCount: totalStudentCount,
    completedCount: completedStudentCount,
    completedRate,
    isAllCompleted,
  } = getAssignmentCompletionSummary(assignmentStatusDetailList ?? []);

  const { barClassName, textClassName } = getProgressColorClassNames({
    progressRate: completedRate,
    isCompleted: isAllCompleted,
  });

  const [isEditingDeadline, setIsEditingDeadline] = useState(false);

  const [deadline, setDeadline] = useState<Dayjs | null>(
    dayjs(formatToKstDate(txInfo.toDate)) ?? null,
  );

  const handleSaveDeadline = async () => {
    if (!deadline) return;

    try {
      await updateAssignmentToDate({
        classId,
        txId: assignmentStatus.txInfo.id,
        toDate: deadline.format('YYYY-MM-DD'),
      });

      dispatch(openAlert({ message: '마감일이 수정되었습니다.' }));
      setIsEditingDeadline(false);

      await fetchList();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  const isAssignmentFinished =
    txInfo.toDoStatus === AssignmentStatusType.COMPLETED ||
    txInfo.toDoStatus === AssignmentStatusType.CLOSED;

  if (isLoading) {
    return <LoadingSpinner message="데이터 조회중" />;
  }

  return (
    <article className="overflow-hidden rounded-xl border border-gray3 bg-white shadow-sm">
      <div className="flex flex-col gap-3 px-4 py-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-purple1 px-2.5 py-1 text-sm font-bold text-purple5">
                {txInfo.book}
              </span>

              <TextWinds type="content_body3" color="gray8">
                {txInfo.lesson}
              </TextWinds>

              <TextWinds
                type="content_body2"
                color="gray9"
                className="font-bold"
              >
                {txInfo.title}
              </TextWinds>

              <span
                className={[
                  'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold',
                  assignmentKindMeta.className,
                ].join(' ')}
              >
                <AssignmentKindIcon className="h-3.5 w-3.5" />
                {assignmentKindMeta.label}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="flex items-center gap-1.5">
                <CalendarDaysIcon className="h-4 w-4 text-gray6" />

                <TextWinds type="caption1" color="gray7">
                  내준 날짜
                </TextWinds>

                <TextWinds type="caption1" color="gray9" className="font-bold">
                  {formatToKstDate(txInfo.fromDate, 'YYYY.MM.DD')}
                </TextWinds>
              </div>

              <div className="flex items-center gap-1.5">
                <CalendarDaysIcon className="h-4 w-4 text-gray6" />

                <TextWinds type="caption1" color="gray7">
                  마감일
                </TextWinds>

                {!isEditingDeadline ? (
                  isAssignmentFinished ? (
                    <TextWinds
                      type="caption1"
                      color="gray9"
                      className="font-bold"
                    >
                      {formatToKstDate(txInfo.toDate, 'YYYY.MM.DD')}
                    </TextWinds>
                  ) : (
                    <button
                      type="button"
                      className="group flex items-center gap-2 rounded-md border border-purple4 bg-white px-2 py-1 hover:bg-purple1"
                      onClick={() => setIsEditingDeadline(true)}
                    >
                      <TextWinds
                        type="caption1"
                        color="gray9"
                        className="font-bold"
                      >
                        {formatToKstDate(txInfo.toDate, 'YYYY.MM.DD')}
                      </TextWinds>

                      <span className="flex items-center gap-1 text-xs font-medium text-purple5">
                        <PencilIcon className="h-3.5 w-3.5" />
                        변경
                      </span>
                    </button>
                  )
                ) : (
                  <div className="flex items-center gap-1">
                    <input
                      type="date"
                      min={dayjs().format('YYYY-MM-DD')}
                      value={deadline.format('YYYY-MM-DD')}
                      onChange={(event) => {
                        const nextDate = dayjs(event.target.value);

                        if (nextDate.isBefore(dayjs(), 'day')) {
                          dispatch(
                            openAlert({
                              message: '오늘 이전 날짜는 선택할 수 없습니다.',
                            }),
                          );
                          return;
                        }

                        setDeadline(nextDate);
                      }}
                      className="h-7 w-[130px] rounded-md border border-purple3 bg-white px-2 text-xs text-gray9 outline-none focus:border-purple5"
                    />

                    <button
                      type="button"
                      className="flex h-7 w-10 items-center justify-center rounded-md bg-purple5 text-sm text-white hover:bg-purple6"
                      onClick={handleSaveDeadline}
                      aria-label="마감일 저장"
                    >
                      저장
                    </button>

                    <button
                      type="button"
                      className="flex h-7 w-10 items-center justify-center rounded-md border border-gray6 text-sm text-gray6 hover:bg-gray1 hover:text-gray9"
                      onClick={() => {
                        setDeadline(dayjs(txInfo.toDate));
                        setIsEditingDeadline(false);
                      }}
                      aria-label="마감일 수정 취소"
                    >
                      취소
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {txInfo.toDoStatus === 'NOT_STARTED' && (
            <button
              type="button"
              className="flex shrink-0 items-center gap-1 self-end rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-500 transition-colors hover:border-red-400 hover:bg-red-50 xl:self-start"
              onClick={() => handleDelete(txId, txInfo.toDoStatus)}
            >
              <TrashIcon className="h-4 w-4" />
              삭제
            </button>
          )}
        </div>

        <div className="mt-1 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="h-2 overflow-hidden rounded-full bg-gray1">
              <div
                className={[
                  'h-full rounded-full transition-all duration-300',
                  barClassName,
                ].join(' ')}
                style={{ width: `${completedRate}%` }}
              />
            </div>
          </div>

          <TextWinds
            type="caption1"
            className={[
              'shrink-0 whitespace-nowrap text-right font-bold',
              textClassName,
            ].join(' ')}
          >
            {completedStudentCount}/{totalStudentCount}명
            {txInfo.todoKind === AssignmentKindType.TEST &&
              ` · 평균 ${txInfo.avgScore ?? 0}점`}
          </TextWinds>
        </div>
      </div>

      <div className="border-t border-gray2 px-4 py-4">
        <AssignmentStatusDetailList
          assignmentKind={txInfo.todoKind}
          assignmentTitle={txInfo.title}
          detailList={assignmentStatusDetailList ?? []}
        />
      </div>
    </article>
  );
};

export default AssignmentStatusByClassCard;
